import React, { useEffect } from "react";
import {
  AnchorLink,
  Banner,
  Button,
  Chip,
  Layout,
  Text,
} from "@kidslivesafe/sc-react";

import "./secondary-accelerate-banner.css";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";

import {
  selectPrimarySubscription,
  setAccelerateBilling,
  selectTrialDetails,
  setShowAccelerateBanner,
  setShowSecondaryAccelerate,
} from "../../../features/subscriptions/subscriptionSlice";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";

const SecondaryAccelerateBanner: React.FC<{
  displayBanner: boolean;
  setDisplayBanner: React.Dispatch<React.SetStateAction<boolean>>;
  reportType: "people" | "vehicle" | "property" | "social" | "phone";
}> = ({ displayBanner, setDisplayBanner, reportType }) => {
  const dispatch = useAppDispatch();

  const trialDetails = useAppSelector(selectTrialDetails);
  const primarySubscription = useAppSelector(selectPrimarySubscription);

  const trialReportThreshold = () => {
    switch (reportType) {
      case "people":
        return trialDetails?.peopleReportThreshold || 1;
      case "vehicle":
        return trialDetails?.vehicleReportThreshold || 1;
      case "property":
        return trialDetails?.propertyReportThreshold || 1;
      case "social":
        return trialDetails?.socialReportThreshold || 1;
      case "phone":
        return trialDetails?.phoneReportThreshold || 1;
      default:
        return 1;
    }
  };
  const primarySubscriptionThreshold = () => {
    switch (reportType) {
      case "people":
        return primarySubscription?.peopleReportThreshold || 1;
      case "vehicle":
        return primarySubscription?.vehicleReportThreshold || 1;
      case "property":
        return primarySubscription?.propertyReportThreshold || 1;
      case "social":
        return primarySubscription?.socialReportThreshold || 1;
      case "phone":
        return primarySubscription?.phoneReportThreshold || 1;
      default:
        return 1;
    }
  };

  useEffect(() => {
    if (displayBanner) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Secondary Report Accelerate Banner",
          EventAction: "Display Banner",
          MetaData: JSON.stringify({ "Report Type": reportType }),
        }),
      );
    }
  }, [displayBanner]);

  return (
    <Banner
      showBanner={displayBanner}
      setShowBanner={setDisplayBanner}
      position="bottom"
      displayX={true}
      closeMiddleware={() => {
        dispatch(
          sendMemberEventAsync({
            EventCategory: "Secondary Report Accelerate Banner",
            EventAction: "Close Banner",
            MetaData: JSON.stringify({ "Report Type": reportType }),
          }),
        );
        setDisplayBanner(false);
        dispatch(setShowAccelerateBanner(false));
      }}
    >
      <Layout
        type="grid"
        gridProps={{
          templateColumns: "2fr-1fr",
          alignItems: "center",
          justifyItems: "flex-end",
          gap: "xxs",
        }}
        breakpointStyles={{
          tablet: {
            type: "grid",
            gridProps: {
              templateColumns: "2fr-1fr",
              alignItems: "center",
              gap: "xxs",
            },
          },
          mobile: {
            type: "flex",
            flexProps: { direction: "column", gap: "xs" },
          },
        }}
      >
        <Layout
          type="flex"
          flexProps={{
            direction: "column",
            gap: "xs",
            alignItems: "flex-start",
          }}
          passThroughClassess="deactivate-banner-content--left"
        >
          <Chip chipText="Report Limit Reached" color="success" />

          <Text size="sm" fontWeight="bold">
            Trial Access: {trialReportThreshold()} Included{" "}
            {reportType.charAt(0).toUpperCase() + reportType.slice(1)}{" "}
            {trialReportThreshold() === 1 ? "Report" : "Reports"}
          </Text>
          <Text size="sm" textAlign="left">
            Your trial includes {trialReportThreshold()}{" "}
            {reportType.charAt(0).toUpperCase() + reportType.slice(1)}{" "}
            {trialReportThreshold() === 1 ? "report" : "reports"}.{" "}
            <AnchorLink
              handleClick={() => {
                dispatch(setShowSecondaryAccelerate(true));
                dispatch(
                  sendMemberEventAsync({
                    EventCategory: "Secondary Report Accelerate Banner",
                    EventAction: "Start Full Membership",
                    MetaData: JSON.stringify({ "Report Type": reportType }),
                  }),
                );
              }}
              role="button"
              inline
              size="sm"
            >
              Start your full membership
            </AnchorLink>{" "}
            to gain instant access to up to 100 reports.
          </Text>
        </Layout>
        <Button
          handleClick={() => {
            dispatch(
              sendMemberEventAsync({
                EventCategory: "Secondary Report Accelerate Banner",
                EventAction: "Close Banner",
                MetaData: JSON.stringify({ "Report Type": reportType }),
              }),
            );
            setDisplayBanner(false);
            dispatch(setShowAccelerateBanner(false));
          }}
          buttonText="Close"
        />
      </Layout>
    </Banner>
  );
};

export default SecondaryAccelerateBanner;
